import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Collapse, DatePicker, Drawer, Form, Input, Popover, Select, Switch, Upload } from "antd";
import Icon from "@mdi/react";
import { mdiCalendarMonth, mdiClose, mdiCrown, mdiFlashOutline, mdiInformationOutline, mdiTrayArrowUp } from "@mdi/js";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import { b2c2bUploadProps, phoneNumberRule } from "../../../../utils/antd.props";
import { currencyDisplay } from "../../../../utils/helpers";
import PhoneNumberInput from "../../../../components/shared/PhoneNumberInput";
import { PAX_TYPES } from "../components/ChooseYourTour";
import { Passenger, ageErrorMessage, ageWindow, dobDisabled } from "./bookingUtils";
import useBreakpoint from "../../../../utils/useBreakpoint";

interface Props {
  open: boolean;
  index: number;
  passenger: Passenger;
  landTour: LandTourDetail;
  departDate: string;
  /** Fare for this passenger's category, shown in the read-only Fare Category field. */
  fare: number;
  countries: { value: string; label: string }[];
  willRegister: boolean;
  /** Passenger 1's contact details, for the "same as Passenger 1" option on passengers 2+. */
  firstContact: { mobile: string; email: string };
  onClose: () => void;
  onSave: (values: Partial<Passenger>) => void;
}


const VISA_TEXT = (
  <div className="gt-visa-text">
    <p>VISA may be required. Kindly reconfirm the VISA requirements with your travel agent.</p>
    <p>If a VISA is required, please apply before making the booking. All payments are non-refundable in the event that the visa is not approved.</p>
  </div>
);

// Open the calendar inside the years this fare category allows (children and infants), not decades away.
const defaultDobView = (category: Passenger["category"], departDate: string, landTour: Props["landTour"]) => {
  const departure = dayjs(departDate);
  const { min, max } = ageWindow(category, landTour);
  if (max === undefined) return departure.subtract(Math.max(min, 30), "year");
  // Middle of the dates that can be picked: from the oldest allowed birth date to the youngest, which can never be after today
  // (an infant leaving soon would otherwise open on a month where every date is disabled).
  const oldest = departure.subtract(max + 1, "year").add(1, "day");
  const youngestAllowed = departure.subtract(min, "year");
  const youngest = youngestAllowed.isAfter(dayjs()) ? dayjs() : youngestAllowed;
  return oldest.add(Math.floor(youngest.diff(oldest, "day") / 2), "day");
};

const UPLOAD_HINT = "(format allowed: .jpg .jpeg .gif .png .pdf .jfif)";
const DESIGNATIONS = ["Mr", "Ms", "Miss", "Mrs", "Mstr", "Mdm"];

function UploadField({ value, name, onChange }: {
  value?: string;
  name?: string;
  onChange?: (value: { signed_id?: string; name?: string }) => void;
}) {
  return (
    <>
      <Upload
        {...b2c2bUploadProps}
        maxCount={1}
        showUploadList={false}
        onChange={(info) => {
          if (info.file.status === "done") onChange?.({ signed_id: (info.file.response as any)?.signed_id, name: info.file.name });
        }}
      >
        <div className={`gt-upload ${value ? "has-file" : ""}`}>
          <span>{value ? name || "File uploaded" : "Please Select"}</span>
          <Icon path={mdiTrayArrowUp} size="20px" />
        </div>
      </Upload>
      <small className="gt-hint">{UPLOAD_HINT}</small>
    </>
  );
}

// Per-passenger edit panel (Figma 10123:275234): "Required Info" first, "Additional Traveller Info" after.
export default function PassengerDrawer({
  open, index, passenger, landTour, departDate, fare, countries, willRegister, firstContact, onClose, onSave,
}: Props) {
  const [form] = Form.useForm();
  const isMobile = useBreakpoint();
  const [visaOpen, setVisaOpen] = useState(false);
  const [same, setSame] = useState(!!passenger.same_as_first);
  const canShare = !!firstContact.mobile && !!firstContact.email;
  const category = PAX_TYPES.find((type) => type.key === passenger.category);

  useEffect(() => {
    if (!open) return;
    setSame(!!passenger.same_as_first);
    form.setFieldsValue({
      ...passenger,
      // Empty strings would count as a chosen value and hide the "Please Select" placeholder.
      nationality: passenger.nationality || undefined,
      designation: passenger.designation || undefined,
      date_of_birth: passenger.date_of_birth ? dayjs(passenger.date_of_birth) : undefined,
      wechat_qrcode: passenger.wechat_qrcode,
      as_passport_photocopy: passenger.as_passport_photocopy,
    });
  }, [open, index]);

  const submit = async () => {
    const values = await form.validateFields();
    onSave({
      ...values,
      same_as_first: index > 0 ? same : undefined,
      nationality: values.nationality || "",
      designation: values.designation || "",
      date_of_birth: values.date_of_birth ? values.date_of_birth.format("YYYY-MM-DD") : "",
      wechat_qrcode: values.wechat_qrcode,
      as_passport_photocopy: values.as_passport_photocopy,
      wechat_qrcode_name: form.getFieldValue("wechat_qrcode_name"),
      as_passport_photocopy_name: form.getFieldValue("as_passport_photocopy_name"),
    });
  };

  const wechatRequired = index === 0 && !!landTour.wechatQrcodeRequired;

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 920}
      closable={false}
      destroyOnClose
      rootClassName="gt-passenger-drawer"
      title={<div className="gt-drawer__head"><span>Passenger {index + 1}</span><button type="button" onClick={onClose} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>}
      footer={isMobile
        ? <div className="gt-drawer__footer is-mobile"><Button size="large" type="primary" block onClick={submit}>Done</Button></div>
        : <div className="gt-drawer__footer"><Button size="large" onClick={onClose}>Cancel</Button><Button size="large" type="primary" onClick={submit}>Done</Button></div>}
    >
      <p className="gt-drawer__intro">Fields marked <b>*</b> are required. Optional fields help us serve you better.</p>
      {index === 0 && willRegister && (
        <div className="gt-banner"><Icon path={mdiCrown} size="20px" color="#ec652f" />You’ll be registered as a member for a more customised experience.</div>
      )}

      <Form form={form} layout="vertical" requiredMark={(label, { required }) => <>{label}{required && !(React.isValidElement(label) && (label.props as any).className === "gt-label-split") && <b className="gt-req">*</b>}</>}>
        <Collapse
          defaultActiveKey={["required", "additional"]}
          ghost
          expandIconPosition="end"
          className="gt-collapse"
          items={[
            {
              key: "required",
              label: <span className="gt-section-title"><i>1</i>Required Info</span>,
              children: (
                <>
                  <Form.Item label="Fare Category" required>
                    <Input disabled value={`${category?.label || ""} (${currencyDisplay("MYR", fare).replace("RM ", "MYR ")})`} />
                  </Form.Item>
                  <div className="gt-form-row">
                    <Form.Item label="Family name/Surname on Passport" name="surname" rules={[{ required: true, message: "Please fill up" }]}>
                      <Input placeholder="e.g. Surname: Smith, Given Name: John" />
                    </Form.Item>
                    <Form.Item label="Given name on Passport" name="given_name" rules={[{ required: true, message: "Please fill up" }]}>
                      <Input placeholder="Given name on Passport" />
                    </Form.Item>
                  </div>
                  <Form.Item
                    label="Date of Birth"
                    name="date_of_birth"
                    extra={<small className="gt-hint">{ageErrorMessage(passenger.category, landTour)}</small>}
                    rules={[{ required: true, message: "Please fill up" }]}
                  >
                    <DatePicker
                      format="DD MMM YYYY"
                      placeholder="Please Select"
                      suffixIcon={<Icon path={mdiCalendarMonth} size="20px" color="#000" />}
                      style={{ width: "100%" }}
                      defaultPickerValue={defaultDobView(passenger.category, departDate, landTour)}
                      disabledDate={(current) => dobDisabled(current, passenger.category, departDate, landTour)}
                    />
                  </Form.Item>
                  {index > 0 && (
                    <label className={`gt-same-contact ${canShare ? "" : "is-disabled"}`}>
                      <Switch
                        size="small"
                        checked={same && canShare}
                        disabled={!canShare}
                        onChange={(checked) => {
                          setSame(checked);
                          if (checked) form.setFieldsValue({ mobile: firstContact.mobile, email: firstContact.email });
                        }}
                      />
                      <span>Contact details same as Passenger 1{!canShare && <small> (fill in Passenger 1’s mobile and email first)</small>}</span>
                    </label>
                  )}
                  <div className="gt-form-row">
                    <Form.Item label="Mobile Number" name="mobile" rules={[{ required: true, message: "Please fill up" }, phoneNumberRule]}>
                      <PhoneNumberInput disabled={same && canShare} />
                    </Form.Item>
                    <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please fill up" }, { type: "email", message: "Enter a valid email" }]}>
                      <Input type="email" placeholder="e.g. johnsmit@gmail.com" disabled={same && canShare} />
                    </Form.Item>
                  </div>
                  <Form.Item
                    label={(
                      <span className="gt-label-split">
                        <span>Nationality<b className="gt-req">*</b></span>
                        {isMobile ? (
                          <button type="button" className="gt-visa-link" onClick={() => setVisaOpen(true)}><Icon path={mdiInformationOutline} size="14px" />VISA Information</button>
                        ) : (
                          <Popover content={VISA_TEXT} title="VISA" trigger={["hover", "click"]} placement="bottomRight" overlayClassName="gt-visa-popover">
                            <button type="button" className="gt-visa-link"><Icon path={mdiInformationOutline} size="14px" />VISA Information</button>
                          </Popover>
                        )}
                      </span>
                    )}
                    name="nationality"
                    rules={[{ required: true, message: "Please fill up" }]}
                  >
                    <Select showSearch placeholder="Please Select" options={countries} optionFilterProp="label" />
                  </Form.Item>
                  {index === 0 && (
                    <Form.Item label="WeChat QR code" name="wechat_qrcode" rules={[{ required: wechatRequired, message: "Please upload your WeChat QR code" }]} required={wechatRequired}>
                      <UploadField
                        name={passenger.wechat_qrcode_name}
                        onChange={(file) => {
                          form.setFieldsValue({ wechat_qrcode: file.signed_id, wechat_qrcode_name: file.name });
                        }}
                      />
                    </Form.Item>
                  )}
                  <Form.Item name="wechat_qrcode_name" hidden><Input /></Form.Item>
                </>
              ),
            },
            {
              key: "additional",
              label: (
                <span className="gt-section-title is-stacked">
                  <em className="gt-chip"><Icon path={mdiFlashOutline} size="14px" />For Faster Check-In</em>
                  <span className="gt-section-title__row"><i>2</i>Additional Traveller Info</span>
                </span>
              ),
              children: (
                <>
                  <Form.Item label="Designation" name="designation">
                    <Select allowClear placeholder="Please Select" options={DESIGNATIONS.map((value) => ({ value, label: value }))} />
                  </Form.Item>
                  <Form.Item label="Passport Number" name="passport_number">
                    <Input placeholder="e.g. A12345678" />
                  </Form.Item>
                  <Form.Item label="Passport Photocopy" name="as_passport_photocopy">
                    <UploadField
                      name={passenger.as_passport_photocopy_name}
                      onChange={(file) => {
                        form.setFieldsValue({ as_passport_photocopy: file.signed_id, as_passport_photocopy_name: file.name });
                      }}
                    />
                  </Form.Item>
                  <Form.Item name="as_passport_photocopy_name" hidden><Input /></Form.Item>
                  <Form.Item label="Remark" name="customer_remark">
                    <Input.TextArea rows={3} placeholder="e.g. I’m a vegan" />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
    <Drawer
      open={visaOpen}
      onClose={() => setVisaOpen(false)}
      placement="bottom"
      height="auto"
      title="Visa Information"
      rootClassName="gt-sheet-drawer"
      zIndex={1100}
      destroyOnClose
    >
      {VISA_TEXT}
    </Drawer>
    </>
  );
}
